// Box lifecycle + fleet rollout client — the UI half of the seven capabilities
// that had a REST route (`createFleetLifecycleRoutes`, `apps/api/src/routes/
// runners.ts`) and an MCP verb (`box`) and no human door at all. `lib/fleet.ts`
// keeps the roster read and the enroll/revoke writes; this file holds
// everything addressed at ONE box's lifecycle (`GET /v1/runners/:id`, drain,
// undrain, set-owner, set-prompt) plus the fleet-wide read a rollout needs
// (`…/releases`) and the trigger itself (`POST …/update`).
//
// THE BRIEFING NEVER COMES BACK WHOLE. `system_prompt` is projected through
// `truncateBoxPrompt` on EVERY door — the MCP verb, this detail read, and the
// write's own echo — 120 chars plus an ellipsis. So a briefing is REPLACE-only
// here, exactly as it is over MCP: `<BoxPromptForm/>` renders the preview
// read-only and never seeds an editable box with it, because saving a prefilled
// preview would cut a 2000-char briefing to 120 chars with nobody asking for it.
//
// CHANNEL IS REQUIRED, NEVER DEFAULTED — the rule the route and the verb both
// state, and the reason `useReleases` IDLES until one is picked: the version
// vocabulary is per channel, so there is nothing honest to offer before that
// choice, and a picker seeded off the wrong channel pins a build that channel
// does not serve.
//
// THE DETAIL READ IS ACCOUNT-FENCED while the roster is not. `GET /v1/runners`
// unions the caller's own private boxes across their orgs (#452); the point read
// takes `(accountId, id)`, so a cross-org row 404s here. That is an ANSWER, not
// a failure: the details panel (`<MachinePanel/>`, #6563) never point-reads a
// `via_org` machine, which it shows read-only off the utilization row.

import type {
  AgentModeKey,
  RunnerOwnerKind,
  RunnerReleaseChannel,
  RunnerStatus,
} from '@developerz/domain';
import { createQuery } from '@tanstack/solid-query';
import { activeAccountId } from './active-account';
import { apiFetch } from './api';
import { type ApiCall, sendApiCall } from './api-call';
import type { TaskRow } from './fleet';
import { createLiveQuery } from './live/live-query';
import type { Page } from './paged';
import { SESSION_TOKEN } from './session';

/**
 * `GET /v1/runners/:id` — `maskBox` (`packages/mcp/src/army/box.ts`), the exact
 * projection the MCP `box get` verb answers with. Restated (not imported)
 * because `@developerz/mcp` is not a dashboard dependency; the field names are
 * the wire contract both doors publish.
 *
 * NOT the roster's `RunnerRow`: that `RunnerRosterResource` projection carries
 * neither `owner_kind` nor `system_prompt` for a PAT-listed row, so it cannot
 * show what the two write routes below just changed. The list answers the table;
 * the point read answers the panel.
 */
export type BoxDetail = {
  readonly id: string;
  readonly status: RunnerStatus;
  readonly display_name: string | null;
  readonly hostname: string | null;
  readonly runner_version: string | null;
  /**
   * Enroll-time hardware as the runner reported it (`BoxSpecs`, camelCase on the wire),
   * or null before enrollment. Optional on read: the details panel (#6563) reads only
   * `arch`, to name the build target its "Up to date" badge compares against.
   */
  readonly specs?: { readonly arch: string } | null;
  /** Task-claim ownership: `org` (this account) | `user` (one member). */
  readonly owner_kind: RunnerOwnerKind;
  readonly owner_id: string | null;
  /** A TRUNCATED PREVIEW of the briefing (120 chars + an ellipsis), never the stored prose. */
  readonly system_prompt: string | null;
  /**
   * The task this box is executing now — the panel's "currently running" line.
   * Derived server-side from the task queue, so it moves with the claim; it was
   * once a runner column nothing wrote and this field was null on every box.
   */
  readonly current_task_id: string | null;
  readonly last_heartbeat_iso: string | null;
  readonly enrolled_iso: string | null;
  /**
   * When an operator drain HELD this box out (`runners.drained_at`), null when not
   * held. A drained box stays out, heartbeat or not, until {@link undrainBox}.
   * Optional because an api that predates the mark omits it (version skew on a
   * roll); absent reads as not drained.
   */
  readonly drained_iso?: string | null | undefined;
  readonly created_iso: string;
  /**
   * Live presence off the fleet KV (`presenceFields`, `packages/mcp/src/box-presence.ts`).
   * Three answers, never collapsed: the beat; `null` = the KV was read and the box
   * holds NO live key; `'unknown'` = the KV could NOT be read, with the read's error
   * in `presence_error` — an unknown box is not an offline one. Narrow with
   * `boxPresence`, never `?.`, which would fold `'unknown'` into "no beat".
   */
  readonly presence?: BoxPresenceBeat | null | 'unknown';
  /** The presence read's failure, present only beside `presence: 'unknown'`. */
  readonly presence_error?: string;
};

export type BoxPresenceBeat = {
  readonly online: boolean;
  readonly active_sessions: number | null;
  readonly version: string | null;
  readonly beat_at: string | null;
};

/** `BoxDetail.presence` narrowed to one discriminated verdict per wire answer. */
export type BoxPresenceVerdict =
  | { readonly kind: 'beat'; readonly beat: BoxPresenceBeat }
  | { readonly kind: 'none' }
  | { readonly kind: 'unknown'; readonly error: string | null };

export function boxPresence(
  box: Pick<BoxDetail, 'presence' | 'presence_error'>,
): BoxPresenceVerdict {
  const p = box.presence;
  if (p === 'unknown') return { kind: 'unknown', error: box.presence_error ?? null };
  if (p === null || p === undefined) return { kind: 'none' };
  return { kind: 'beat', beat: p };
}

export const BOX_DETAIL_QUERY_KEY = 'box_detail';

/**
 * One box in full. Idle until a box is selected — the details panel gates on its
 * id. A 404 covers an unknown box, another account's box AND a cross-org row
 * (the fence above), and the panel reads it as an answer rather than a failure.
 * LIVE (`createLiveQuery`, #7612): re-read when THIS machine's `machines` row moves.
 */
export function useBoxDetail(boxId: () => string | null, token: string | null = SESSION_TOKEN) {
  return createLiveQuery<BoxDetail>(() => {
    const id = boxId();
    return {
      queryKey: [BOX_DETAIL_QUERY_KEY, id, token, activeAccountId()],
      enabled: token !== null && id !== null,
      live: id === null ? [] : [{ entity: 'machine', id }],
      queryFn: () => apiFetch<BoxDetail>(`/v1/runners/${encodeURIComponent(id ?? '')}`, token),
    };
  }, token).query;
}

/**
 * `POST /v1/runners/:id/drain` — EVACUATE, never kill. The box stays enrolled
 * and its seat stays held (that is revoke); its in-flight work is re-queued.
 * `flipped_offline: false` is the benign already-drained case, not a failure.
 */
export type BoxDrained = {
  readonly box_id: string;
  readonly status: RunnerStatus;
  readonly flipped_offline: boolean;
  readonly reenqueued_task_ids: readonly string[];
};

export function drainBox(boxId: string, token: string | null = SESSION_TOKEN): Promise<BoxDrained> {
  return sendApiCall<BoxDrained>(boxDrainCall(boxId), token);
}

/** The drain as one value — fired by `drainBox`, shown by `<CopyAsCall/>`. */
export function boxDrainCall(boxId: string): ApiCall {
  return {
    capability: 'box.drain',
    method: 'POST',
    path: `/v1/runners/${encodeURIComponent(boxId)}/drain`,
    mcp: { kind: 'verb', verb: 'box.drain', arguments: { box_id: boxId } },
  };
}

/**
 * `POST /v1/runners/:id/undrain` — clear the drain mark so the box REJOINS the fleet
 * on the presence sweep after its next live heartbeat; it never marks the box online
 * itself. `cleared: false` is a box that was not drained (nothing written), benign.
 */
export type BoxUndrained = {
  readonly box_id: string;
  readonly status: RunnerStatus;
  readonly cleared: boolean;
};

export function undrainBox(
  boxId: string,
  token: string | null = SESSION_TOKEN,
): Promise<BoxUndrained> {
  return sendApiCall<BoxUndrained>(boxUndrainCall(boxId), token);
}

/** The undrain as one value — fired by `undrainBox`, shown by `<CopyAsCall/>`. */
export function boxUndrainCall(boxId: string): ApiCall {
  return {
    capability: 'box.undrain',
    method: 'POST',
    path: `/v1/runners/${encodeURIComponent(boxId)}/undrain`,
    mcp: { kind: 'verb', verb: 'box.undrain', arguments: { box_id: boxId } },
  };
}

/**
 * `POST /v1/runners/:id/decommission` (`box.decommission`) — take the machine OUT in one
 * act: drain (its task re-queued now), revoke, audit `runner.decommissioned`. A 200 with a
 * non-null `cascade_failed` is a machine that IS removed whose access tokens still need
 * one idempotent retry — through {@link revokeRunner} (`./fleet`), never a second
 * decommission, which answers 404 on a revoked machine.
 */
export type BoxDecommissioned = {
  readonly box_id: string;
  readonly status: RunnerStatus;
  readonly reenqueued_task_ids: readonly string[];
  readonly cascade_failed: string | null;
};

export function decommissionBox(
  boxId: string,
  token: string | null = SESSION_TOKEN,
): Promise<BoxDecommissioned> {
  return sendApiCall<BoxDecommissioned>(boxDecommissionCall(boxId), token);
}

/** The decommission as one value — fired by `decommissionBox`. */
export function boxDecommissionCall(boxId: string): ApiCall {
  return {
    capability: 'box.decommission',
    method: 'POST',
    path: `/v1/runners/${encodeURIComponent(boxId)}/decommission`,
    mcp: { kind: 'verb', verb: 'box.decommission', arguments: { box_id: boxId } },
  };
}

/**
 * `PATCH /v1/runners/:id` — rename a box, or send `null` to RESTORE the generated
 * Adjective Noun name the server fills for an unnamed one (#6422). `null` is the
 * reversal, not a missing field: the key rides on every call, verbatim.
 *
 * NO MCP HALF: the `box` verb has no rename action (capability manifest,
 * `box.rename`), so this is the one declared call that files `mcp: 'none'` —
 * the copy button says so rather than printing a verb the server would refuse.
 */
export type BoxRenamed = {
  readonly id: string;
  readonly display_name: string | null;
};

export function renameBox(
  boxId: string,
  name: string | null,
  token: string | null = SESSION_TOKEN,
): Promise<BoxRenamed> {
  return sendApiCall<BoxRenamed>(boxRenameCall(boxId, name), token);
}

/** The rename as one value — fired by `renameBox`, shown by `<CopyAsCall/>`. */
export function boxRenameCall(boxId: string, name: string | null): ApiCall {
  return {
    capability: 'box.rename',
    method: 'PATCH',
    path: `/v1/runners/${encodeURIComponent(boxId)}`,
    body: { display_name: name },
    mcp: { kind: 'none', reason: 'The box verb has no rename action.' },
  };
}

/**
 * `POST /v1/runners/:id/work` (#6767) — send a GitHub pull request or issue to ONE machine
 * under one role; 201 is the minted task, held on the machine (`target_runner_id`) for ten
 * minutes. Each refusal is a 409/422 problem carrying `reason` and `role`, which the card's
 * form renders (`refusalLine`, `./send-work`). `role` is a mode key (`worker` for Coder).
 */
export type BoxSendBody = {
  readonly link: string;
  readonly role: AgentModeKey;
  readonly note?: string;
};

export function sendBoxWork(
  boxId: string,
  body: BoxSendBody,
  idempotencyKey: string,
  token: string | null = SESSION_TOKEN,
): Promise<TaskRow> {
  return sendApiCall<TaskRow>(boxSendCall(boxId, body, idempotencyKey), token);
}

/**
 * The send as one value — fired by `sendBoxWork`. The key rides as the `Idempotency-Key`
 * header, minted per submit by the form, and never in the MCP `arguments`, which
 * `mcpSnippet` prints.
 */
export function boxSendCall(boxId: string, body: BoxSendBody, idempotencyKey: string): ApiCall {
  return {
    capability: 'box.send',
    method: 'POST',
    path: `/v1/runners/${encodeURIComponent(boxId)}/work`,
    body,
    mcp: { kind: 'verb', verb: 'box.send', arguments: { box_id: boxId, ...body } },
    idempotencyKey,
  };
}

/** The whole ownership pair, as `PUT /v1/runners/:id/owner` replaces it. */
export type BoxOwnership = {
  readonly owner_kind: RunnerOwnerKind;
  /** `accounts.id` (org) | `users.id` (user). Null only on a pre-ownership row. */
  readonly owner_id: string | null;
};

/**
 * Retarget which work a box may claim. The API is the arbiter of BOTH gates —
 * the shape rule (both kinds require an id) and the
 * membership rule (a `user` who is not a member, an `org` that is not this
 * account) — and answers each as its own 422 sentence, rendered inline.
 */
export function setBoxOwner(
  boxId: string,
  ownership: BoxOwnership,
  token: string | null = SESSION_TOKEN,
): Promise<{ readonly box_id: string } & BoxOwnership> {
  return sendApiCall(boxOwnerCall(boxId, ownership), token);
}

/** The retarget as one value — fired by `setBoxOwner`, shown by `<CopyAsCall/>`. */
export function boxOwnerCall(boxId: string, ownership: BoxOwnership): ApiCall {
  return {
    capability: 'box.set_owner',
    method: 'PUT',
    path: `/v1/runners/${encodeURIComponent(boxId)}/owner`,
    body: { ...ownership },
    mcp: { kind: 'verb', verb: 'box.set_owner', arguments: { box_id: boxId, ...ownership } },
  };
}

/** `PUT /v1/runners/:id/prompt` — the echo is the same 120-char preview the read serves. */
export type BoxPromptSet = {
  readonly box_id: string;
  readonly system_prompt: string | null;
  readonly cleared: boolean;
};

/**
 * Replace or clear a box's briefing. `null` CLEARS; a string replaces the whole
 * prose. Nothing infers a clear from an empty box — `null` is a deliberate act
 * on every door (the prompt-var rule), and here it is a separate control.
 */
export function setBoxPrompt(
  boxId: string,
  systemPrompt: string | null,
  token: string | null = SESSION_TOKEN,
): Promise<BoxPromptSet> {
  return sendApiCall<BoxPromptSet>(boxPromptCall(boxId, systemPrompt), token);
}

/**
 * The briefing write as one value — fired by `setBoxPrompt`, shown by
 * `<CopyAsCall/>`. `null` rides both doors verbatim because it is the CLEAR, and
 * a snippet that dropped the field would read as "leave it alone".
 */
export function boxPromptCall(boxId: string, systemPrompt: string | null): ApiCall {
  return {
    capability: 'box.set_prompt',
    method: 'PUT',
    path: `/v1/runners/${encodeURIComponent(boxId)}/prompt`,
    body: { system_prompt: systemPrompt },
    mcp: {
      kind: 'verb',
      verb: 'box.set_prompt',
      arguments: { box_id: boxId, system_prompt: systemPrompt },
    },
  };
}

/**
 * `PUT /v1/runners/:id/max-slots` (#6740) — set the per-box "jobs at once" cap, the most
 * claims the box may hold at once. The bounds are the domain's (`RUNNER_MAX_SLOTS_*`,
 * `entities/runner/max-slots.ts`), derived by the db CHECK, this route body and the MCP
 * verb alike. The echo carries the cap it REPLACED — the audit row records the pair too.
 * There is no unset state to send: "reset" is a cap write of `RUNNER_MAX_SLOTS_DEFAULT`.
 */
export type BoxMaxSlotsSet = {
  readonly runner_id: string;
  readonly max_slots: number;
  readonly previous_max_slots: number;
};

export function setBoxMaxSlots(
  boxId: string,
  maxSlots: number,
  token: string | null = SESSION_TOKEN,
): Promise<BoxMaxSlotsSet> {
  return sendApiCall<BoxMaxSlotsSet>(boxMaxSlotsCall(boxId, maxSlots), token);
}

/** The cap write as one value — fired by `setBoxMaxSlots`, shown by `<CopyAsCall/>`. */
export function boxMaxSlotsCall(boxId: string, maxSlots: number): ApiCall {
  return {
    capability: 'box.max_slots',
    method: 'PUT',
    path: `/v1/runners/${encodeURIComponent(boxId)}/max-slots`,
    body: { max_slots: maxSlots },
    mcp: {
      kind: 'verb',
      verb: 'box.set_max_slots',
      arguments: { box_id: boxId, max_slots: maxSlots },
    },
  };
}

/**
 * One row of `GET /v1/runners/releases`. The route serves TWO projections off
 * one path — a tenant's installable rows, or an operator's whole ledger — and
 * `view` names which arrived. The fields below are the tenant subset, which the
 * ledger view is a superset of; `yanked_at` rides only on the ledger view, where
 * a non-null stamp means the build is HALTED.
 */
export type ReleaseRow = {
  readonly version: string;
  readonly channel: RunnerReleaseChannel;
  readonly target: string;
  readonly published_at: string;
  readonly yanked_at?: string | null;
};

/**
 * The ledger on the canonical `{data, next_cursor}` envelope, with the projection
 * name and the bound riding beside it. `next_cursor` is always null because the
 * route publishes no `cursor` param, NOT because it served every build: it serves
 * the newest `BOX_RELEASES_PAGE` rows (`@developerz/mcp`) on the asked-for channel,
 * and `truncated` is the one signal that older builds exist past that head
 * (`apps/api` `runners.ts`, the `box.releases` route). A picker built from a
 * truncated catalog must say so (`FleetRolloutModal`).
 */
export type ReleaseCatalog = Page<ReleaseRow> & {
  readonly view: 'ledger' | 'installable';
  readonly truncated: boolean;
};

export const BOX_RELEASES_QUERY_KEY = 'box_releases';

/**
 * The builds installable on ONE channel. Idle until a channel is picked — see
 * the channel rule at the top of this file. Not polled: the ledger changes when
 * CI publishes, and this read exists to fill a picker in an open dialog.
 */
export function useReleases(
  channel: () => RunnerReleaseChannel | null,
  token: string | null = SESSION_TOKEN,
) {
  return createQuery<ReleaseCatalog>(() => ({
    queryKey: [BOX_RELEASES_QUERY_KEY, channel(), token, activeAccountId()],
    enabled: token !== null && channel() !== null,
    queryFn: () =>
      apiFetch<ReleaseCatalog>(
        `/v1/runners/releases?channel=${encodeURIComponent(channel() ?? '')}`,
        token,
      ),
  }));
}

/**
 * The versions a rollout may PIN, newest first — the picker's vocabulary.
 *
 * Two rules, both load-bearing. A HALTED build is dropped: an operator reading
 * the ledger view sees yanked rows on purpose (it is where the un-yank repair
 * starts), and pinning one would roll out a build the platform pulled. And one
 * version ships several targets (`linux-x64`, `linux-arm64`), so the rows are
 * deduped BY VERSION — the trigger names a build, not a build for one
 * architecture. Server order is newest-first (`listRunnerReleases`) and is kept
 * rather than re-sorted here: the ledger's order is the ledger's to decide.
 */
export function releaseVersions(catalog: ReleaseCatalog | undefined): readonly string[] {
  const seen = new Set<string>();
  for (const row of catalog?.data ?? []) {
    if ((row.yanked_at ?? null) !== null) continue;
    seen.add(row.version);
  }
  return [...seen];
}

/**
 * `POST /v1/runners/update` 202 body. A 202 is NOT an updated fleet: the rollout
 * runs for minutes to hours (canary, soak, waves) and lands as `runner.update_*`
 * audit rows while `GET /v1/runners/versions` (MCP `box versions`) shows the fleet
 * converging; the console no longer renders that spread (#6559).
 *
 * `started: false` means a rollout was already running on this channel and this
 * trigger JOINED it — benign, and then `version` is the one actually rolling,
 * which is not necessarily the one this call asked for.
 */
export type RolloutStarted = {
  readonly rollout_id: string;
  readonly channel: RunnerReleaseChannel;
  /** The version actually rolling; `null` ⇒ the channel's newest installable build. */
  readonly version: string | null;
  readonly started: boolean;
  readonly note?: string;
};

/**
 * Roll the visible fleet onto a build. `version` omitted ⇒ the channel's newest.
 * A deployment with no rollout lane answers 503 with its own sentence
 * (`BOX_UPDATE_UNCONFIGURED`) — one deployment fact, one wording, on both doors.
 */
export function startRollout(
  input: { readonly channel: RunnerReleaseChannel; readonly version?: string },
  token: string | null = SESSION_TOKEN,
): Promise<RolloutStarted> {
  return apiFetch<RolloutStarted>('/v1/runners/update', token, {
    method: 'POST',
    body: JSON.stringify({
      channel: input.channel,
      ...(input.version !== undefined ? { version: input.version } : {}),
    }),
  });
}
